import { useEffect } from 'react'
import type { SlideInfo } from '@/components/DeckViewer'
import './SpeakerNotes.css'

interface SpeakerNotesOverlayProps {
  slides: SlideInfo[]
  currentSlide: number
  onPrev: () => void
  onNext: () => void
  onClose: () => void
  onOpenWindow: () => void
}

export function SpeakerNotesOverlay({
  slides,
  currentSlide,
  onPrev,
  onNext,
  onClose,
  onOpenWindow,
}: SpeakerNotesOverlayProps) {
  const slide = slides[currentSlide]
  const notes = slide?.notes || ''
  const slideCount = slides.length

  // Keyboard navigation
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft' && currentSlide > 0) onPrev()
      if (e.key === 'ArrowRight' && currentSlide < slideCount - 1) onNext()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [currentSlide, slideCount, onClose, onPrev, onNext])

  return (
    <div className="notes-overlay" onClick={onClose}>
      <div className="notes-modal" onClick={e => e.stopPropagation()}>
        <div className="notes-header">
          <span className="notes-slide-num">Slide {currentSlide + 1} / {slideCount}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="notes-open-window" onClick={onOpenWindow} title="Open in separate window">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              Window
            </button>
            <button className="notes-close" onClick={onClose}>&times;</button>
          </div>
        </div>

        {slide?.label && <div className="notes-label">{slide.label}</div>}
        <div className="notes-title">{slide?.title || `Slide ${currentSlide + 1}`}</div>

        <div className="notes-divider" />

        {notes ? (
          <div className="notes-text">{notes.replace(/\\n/g, '\n')}</div>
        ) : (
          <div className="notes-empty">No speaker notes for this slide.</div>
        )}

        <div className="notes-nav">
          <button className="notes-nav-btn" onClick={onPrev} disabled={currentSlide <= 0}>&larr; Prev</button>
          <button className="notes-nav-btn" onClick={onNext} disabled={currentSlide >= slideCount - 1}>Next &rarr;</button>
        </div>
      </div>
    </div>
  )
}
